import React, { useState, useEffect } from "react";
import { FileText, Download, Loader2 } from "lucide-react";
import { Card, CardHeader, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { UserDocument, DOCUMENT_TYPES, DocumentType } from "../gestion/user-details/documents/types";
import { formatFileSize, getFileIcon } from "../gestion/user-details/documents/utils";

interface UserDocumentsProps {
  userId: string;
}

export const UserDocuments: React.FC<UserDocumentsProps> = ({ userId }) => {
  const [documents, setDocuments] = useState<UserDocument[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDocuments();
  }, [userId]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("user_documents")
        .select(`
          *,
          uploader:profiles!user_documents_uploaded_by_fkey (
            name,
            email
          )
        `)
        .eq("user_id", userId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setDocuments(data || []);
    } catch (error) {
      console.error("Erreur lors du chargement des documents:", error);
      toast.error("Erreur lors du chargement des documents");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (doc: UserDocument) => {
    try {
      const { data, error } = await supabase.storage
        .from("user-documents")
        .download(doc.file_path);

      if (error) throw error;

      // Créer un lien de téléchargement
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.file_name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Erreur lors du téléchargement:", error);
      toast.error("Erreur lors du téléchargement");
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <h2 className="text-lg font-bold">Mes Documents</h2>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-6 w-6 text-[#BD1E28] animate-spin" />
            <span className="ml-2">Chargement des documents...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <h2 className="text-lg font-bold">Mes Documents</h2>
      </CardHeader>
      <CardContent>
        {documents.length === 0 ? (
          <div className="text-center py-4 text-gray-500">
            <FileText className="w-12 h-12 mx-auto mb-2 text-gray-300" />
            <p>Aucun document disponible</p>
          </div>
        ) : (
          <div className="space-y-3">
            {documents.map((doc) => (
              <div key={doc.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="text-[#BD1E28]">
                    {getFileIcon(doc.file_type)}
                  </div>
                  <div>
                    <h4 className="font-medium text-[#333333]">{doc.file_name}</h4>
                    <p className="text-sm text-[#666666]">
                      {DOCUMENT_TYPES[doc.document_type as DocumentType]} • {formatFileSize(doc.file_size)}
                    </p>
                    {doc.description && (
                      <p className="text-sm text-[#666666]">{doc.description}</p>
                    )}
                    <p className="text-xs text-[#888888]">
                      Ajouté le {new Date(doc.created_at).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDownload(doc)}
                >
                  <Download className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
